// components/inventory/UnitCard.tsx
import { useAppTheme } from "@/hooks/useAppTheme";
import { useAppDispatch } from "@/store/hooks";
import { deleteUnit, updateUnit } from "@/store/slices/unitSlice";
import { Ionicons } from "@expo/vector-icons";
import { router } from "expo-router";
import { useState } from "react";
import { Alert, StyleSheet, Text, TouchableOpacity, View } from "react-native";

export default function UnitCard({ unit, onRefresh }: any) {
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const isActive = unit.status === "active";

  const handleToggle = async () => {
    const newStatus = isActive ? "inactive" : "active";
    setLoading(true);
    const result = await dispatch(
      updateUnit({ id: unit.id, data: { status: newStatus } }),
    );
    if (updateUnit.fulfilled.match(result)) onRefresh();
    setLoading(false);
  };

  const handleDelete = () => {
    Alert.alert("Delete", `Delete ${unit.name}?`, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Delete",
        style: "destructive",
        onPress: async () => {
          setLoading(true);
          const result = await dispatch(deleteUnit(unit.id));
          if (deleteUnit.fulfilled.match(result)) onRefresh();
          setLoading(false);
        },
      },
    ]);
  };

  const styles = StyleSheet.create({
    card: {
      backgroundColor: colors.background,
      borderRadius: 12,
      padding: spacing.md,
      marginBottom: spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
    },
    header: { flexDirection: "row", justifyContent: "space-between" },
    name: { fontSize: 16, fontWeight: "600", color: colors.text },
    symbol: { fontSize: 14, color: colors.primary, marginTop: 2 },
    statusBadge: { flexDirection: "row", alignItems: "center", marginTop: 8 },
    statusDot: { width: 8, height: 8, borderRadius: 4, marginRight: 6 },
    statusText: { fontSize: 12, fontWeight: "500" },
    actions: {
      flexDirection: "row",
      marginTop: spacing.md,
      paddingTop: spacing.sm,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    actionButton: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      paddingVertical: 6,
    },
    actionText: { fontSize: 12, marginLeft: 4, color: colors.textLight },
  });

  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <View>
          <Text style={styles.name}>{unit.name}</Text>
          <Text style={styles.symbol}>{unit.symbol}</Text>
        </View>
        <TouchableOpacity
          onPress={() => router.push(`/(app)/inventory/units/${unit.id}`)}
        >
          <Ionicons name="chevron-forward" size={20} color={colors.textLight} />
        </TouchableOpacity>
      </View>
      <View style={styles.statusBadge}>
        <View
          style={[
            styles.statusDot,
            { backgroundColor: isActive ? "#22c55e" : "#ef4444" },
          ]}
        />
        <Text
          style={[
            styles.statusText,
            { color: isActive ? "#22c55e" : "#ef4444" },
          ]}
        >
          {isActive ? "Active" : "Inactive"}
        </Text>
      </View>
      <View style={styles.actions}>
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => router.push(`/(app)/inventory/units/${unit.id}`)}
        >
          <Ionicons name="create-outline" size={18} color={colors.primary} />
          <Text style={styles.actionText}>Edit</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.actionButton} onPress={handleToggle}>
          <Ionicons
            name={isActive ? "eye-off-outline" : "eye-outline"}
            size={18}
            color="#f59e0b"
          />
          <Text style={styles.actionText}>
            {isActive ? "Deactivate" : "Activate"}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.actionButton} onPress={handleDelete}>
          <Ionicons name="trash-outline" size={18} color={colors.danger} />
          <Text style={[styles.actionText, { color: colors.danger }]}>
            Delete
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}
